import { useState, useRef } from "react"
import {
  AlertDialog,
  AlertDialogLabel,
  AlertDialogDescription,
} from "@reach/alert-dialog"
import { Button } from "@planningcenter/doxy-web"
import { string, func, bool } from "prop-types"
import { paymentMethodProps } from "source/payments/payment-method-prop-types"
import { VerifyAmounts } from "./VerifyAmounts"
import { VerifyDescriptorCode } from "./VerifyDescriptorCode"
import { PAYMENT_URLS } from "source/payments/utils"
import { api } from "source/registrations/api/client"
import { AlertMessage } from "source/shared/components"
import { useQuery } from "@tanstack/react-query"
import { fetchPaymentMethodWithPendingDonation } from "source/giving/profile/queries"

export const VerifyAccountDialog = ({
  orgName,
  paymentMethod,
  testMode,
  onSuccess,
  onConfirm,
  onCancel,
}) => {
  const { last4, verificationMethod } = paymentMethod
  const cancelRef = useRef(null)
  const [inputValid, setInputValid] = useState(false)

  const [descriptorCode, setDescriptorCode] = useState("")
  const [microdeposit1, setMicrodeposit1] = useState("")
  const [microdeposit2, setMicrodeposit2] = useState("")

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  function codeFromError(error) {
    if (error && "errors" in error && Array.isArray(error.errors)) {
      return error.errors[0].status
    }
    return undefined
  }

  const {
    data: givingPaymentMethod,
    isLoading: _loadingPendingGivingPaymentMethod,
  } = useQuery({
    queryKey: ["PaymentMethod", paymentMethod.stripeIdentifier],
    queryFn: () =>
      fetchPaymentMethodWithPendingDonation(paymentMethod.stripeIdentifier),
    retry: (_failureCount, error) => {
      // Don't retry if error is 404 or 401
      if (error instanceof TypeError && error.message === "Failed to fetch") {
        return false
      }

      const statusCode = codeFromError(error)
      if (statusCode === "404") return false
      return true
    },
  })

  const handleSubmitAccountVerification = async () => {
    const body = {
      data: {
        attributes:
          verificationMethod === "stripe_connections_microdeposit_amounts"
            ? { amounts: [microdeposit1, microdeposit2] }
            : { descriptor_code: descriptorCode },
      },
    }
    try {
      setError(null)
      setSubmitting(true)
      const { data, errors } = await api.post(
        PAYMENT_URLS.verfiyPaymentMethod(paymentMethod.id),
        body,
      )
      if (errors) {
        setError(
          "There was a problem verifying your bank account. Please try again.",
        )
      } else if (data) {
        onSuccess()
        onConfirm()
      }
    } catch {
      setError(
        "There was a problem verifying your bank account. Please try again.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  const verifyFormProps = {
    last4,
    orgName,
    testMode,
    setInputValid,
    onVerify: () => {},
  }

  const verifyFormLookup = {
    stripe_connections_microdeposit_amounts: (
      <VerifyAmounts
        {...verifyFormProps}
        microdeposit1={microdeposit1}
        microdeposit2={microdeposit2}
        setMicrodeposit1={setMicrodeposit1}
        setMicrodeposit2={setMicrodeposit2}
      />
    ),
    stripe_connections_microdeposit_descriptor_code: (
      <VerifyDescriptorCode
        {...verifyFormProps}
        descriptorCode={descriptorCode}
        setDescriptorCode={setDescriptorCode}
      />
    ),
  }

  const formatDonationAmount = (donation) => {
    const amount = donation.amount_cents / 100
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: donation.amount_currency || "USD",
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    })
  }

  return (
    <AlertDialog leastDestructiveRef={cancelRef}>
      <AlertDialogLabel>Enter deposit code</AlertDialogLabel>
      <AlertDialogDescription>
        {givingPaymentMethod?.pendingDonation && (
          <div className="info-alert alert">
            <p className="mb-0">
              Your {formatDonationAmount(givingPaymentMethod?.pendingDonation)}{" "}
              donation on{" "}
              {formatDate(givingPaymentMethod?.pendingDonation.received_at)},
              made while connecting this bank account, will finish processing
              once verification is complete.
            </p>
          </div>
        )}
        {verifyFormLookup[verificationMethod]}
        {error && (
          <div className="mt-3">
            <AlertMessage type="error">{error}</AlertMessage>
          </div>
        )}
        <div className="d-f jc-fe mt-3 g-1">
          <Button
            text="Cancel"
            variant="naked"
            onClick={onCancel}
            ref={cancelRef}
          />
          <Button
            text="Verify account"
            variant="primary"
            disabled={!inputValid}
            isLoading={submitting}
            onClick={handleSubmitAccountVerification}
          />
        </div>
      </AlertDialogDescription>
    </AlertDialog>
  )
}

VerifyAccountDialog.propTypes = {
  orgName: string.isRequired,
  paymentMethod: paymentMethodProps.isRequired,
  testMode: bool,
  onSuccess: func.isRequired,
  onConfirm: func.isRequired,
  onCancel: func.isRequired,
}
